import { FC } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button,
} from '@mui/material';

import { Logo } from '@shared/components/Logo';

import HeartPulse from './HeartPulse';

interface CreditCardProps {
  credit: Tributes.AvailableCredit;
  onCreateLGCYClick: () => void
}

export const CreditCard: FC<CreditCardProps> = ({ 
  credit,
  onCreateLGCYClick,
}) => {
  const createdAt = new Date(credit.created).toLocaleDateString();

  return (
    <Card 
      sx={{
        width: '20rem',
        maxWidth: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgb(229, 231, 235)',
        borderRadius: '24px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {/* LGCY PET Logo Header */}
      <Box 
        sx={{ 
          height: '8rem',
          bgcolor: 'rgb(249, 250, 251)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <HeartPulse />
            <Logo height={40} width="auto" />
            <Typography 
              variant="caption" 
              component="span" 
              sx={{ 
                color: 'rgb(156, 163, 175)',
                ml: 0.5,
                verticalAlign: 'top',
              }}
            >
              ™
            </Typography>
          </Box>
        </Box>
      </Box>

      <CardContent 
        sx={{ 
          p: 3, 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 600, 
              color: 'text.primary',
              mb: 1,
            }}
          >
            Unnamed LGCY
          </Typography>
          <Box 
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: 500,
              bgcolor: 'rgb(243, 244, 246)',
              color: 'rgb(107, 114, 128)',
            }}
          >
            Unused
          </Box>
        </Box>

        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgb(107, 114, 128)',
            mb: 3,
          }}
        >
          Created: {createdAt}
        </Typography>

        <Box 
          sx={{ 
            display: 'flex',
            gap: 0,
            mt: 'auto',
          }}
        >
          <Button
            component={Link}
            to={`/gift/regift/${credit.id}/`}
            variant="contained"
            sx={{
              flex: 1,
              bgcolor: 'rgb(156, 39, 176)',
              '&:hover': {
                bgcolor: 'rgb(126, 34, 146)',
              },
              color: 'white',
              minHeight: '48px',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderTopLeftRadius: '12px',
              borderBottomLeftRadius: '12px',
              fontWeight: 500,
              textTransform: 'none',
            }}
            aria-label="Gift this credit to someone else"
          >
            + Gift LGCY
          </Button>

          <Button
            component={Link}
            to="/tributes/create/details"
            variant="contained"
            sx={{
              flex: 1,
              bgcolor: 'rgb(242, 110, 99)',
              '&:hover': {
                bgcolor: 'rgb(220, 95, 85)',
              },
              color: 'white',
              minHeight: '48px',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
              fontWeight: 500,
              textTransform: 'none',
            }}
            aria-label="Use this credit to create an LGCY"
            onClick={onCreateLGCYClick}
          >
            + Create LGCY
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CreditCard;
